/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React from "react"
import {
  Button,
  Container,
  FlexContainer,
  Icon,
  sizing,
  Stack,
  Text,
  TextLink,
  useBreakpointComponent
} from "@carrotfertility/carotene"
import styled from "styled-components"
import { Provider } from "../../types/Providers"
import { Link } from "react-router-dom"
import { useUserContext } from "../../context/UserContext"
import { RetinaImage } from "../RetinaImage"
import { ProviderTypeTags } from "./ProviderTypeTags"
import { ProviderTypeTagsForExtendedNetworks } from "./ProviderTypeTagsForExtendedNetworks"
import { useCompanyContext } from "pages/provider-finder/context/CompanyContextProvider"
import Settings from "pages/provider-finder/lib/Settings"
import { Benefit } from "pages/provider-finder/types/Benefit"
import { getPiiProps } from "services/tracking"
import { FormattedMessage, useIntl } from "react-intl"
import { useGetLocalizedDistanceStringWithUnits } from "pages/provider-finder/hooks/useGetLocalizedDistanceStringWithUnits"
import carrotPartnerIndicator from "../../resources/images/carrot-partner-indicator.svg"

const ProviderContactLink = styled(TextLink)`
  font-size: ${sizing.font.small};
`

const DirectionsLink = ({ address, searchLocation }: { address: string; searchLocation: string }): JSX.Element => {
  const getGoogleDirections = (address: string): string => {
    // The structure of the url is `/maps/dir/<starting point>/<destination>`
    const googleBaseUrl = "https://www.google.com/maps/dir"
    const encodedSearchLocation = encodeURIComponent(searchLocation)
    const encodedAddress = encodeURIComponent(address)
    return `${googleBaseUrl}/${encodedSearchLocation}/${encodedAddress}`
  }
  return (
    <ProviderContactLink className="provider-directions-link" to={getGoogleDirections(address)} target="_blank">
      <FormattedMessage
        defaultMessage="Directions"
        description="Clicking on the directions link will open up Google Maps to get the directions to this provider's location"
      />
    </ProviderContactLink>
  )
}

const BookingButton = styled(Button)`
  background-color: #fcf3e5 !important;
  &:hover {
    background-color: #fae6cc !important;
  }
`

const LocalizedDistance = ({ distanceInKm }: { distanceInKm: number }): JSX.Element => {
  const { companyCountryCode } = useUserContext()
  return (
    <Text size="10px" weight="bolder">
      {useGetLocalizedDistanceStringWithUnits({
        distanceInKm,
        countryCode: companyCountryCode,
        numberOfFractionDigits: 1
      })}
    </Text>
  )
}

type ProviderDetailsProps = {
  provider: Provider
  rank?: number
  searchLocation: string
  benefit: Benefit
}

export function ProviderDetails({ provider, rank, searchLocation, benefit }: ProviderDetailsProps): JSX.Element {
  const {
    providerName,
    parentIds,
    address,
    phone,
    website,
    distanceInKm,
    carrotPartner,
    locationMayVary,
    providerTypeIds,
    networkIds
  } = provider

  const { companyId } = useCompanyContext()
  const networkConfigForEnv = Settings.COMPANY_IDS_WITH_TIERED_PROVIDER_NETWORK

  const intl = useIntl()

  const mainContent = (
    <FlexContainer {...getPiiProps()} direction="column">
      {networkIds && networkIds.length && networkConfigForEnv.includes(parseInt(companyId, 10)) ? (
        <ProviderTypeTagsForExtendedNetworks
          parentIds={parentIds}
          providerTypeIds={providerTypeIds}
          networkIds={networkIds}
          benefit={benefit}
        />
      ) : (
        <ProviderTypeTags parentIds={parentIds} providerTypeIds={providerTypeIds} benefit={benefit} />
      )}
      <Text size="20px" font="publicoHeadline" weight="bolder" sx={{ marginBottom: "8px" }}>
        {providerName}
      </Text>
      <Text size="small" sx={{ wordBreak: "break-word" }}>
        {address}
      </Text>
      {locationMayVary && (
        <FlexContainer sx={{ padding: "8px 6px" }} gap="6px" alignItems="center">
          <RetinaImage name={"icn-pin"} height={24} width={24} />
          <Text size="small" fontStyle="italic">
            <FormattedMessage defaultMessage="Contact the provider to confirm. They may offer services virtually or from multiple locations." />
          </Text>
        </FlexContainer>
      )}
      <Stack direction="row" sx={{ marginTop: "12px" }}>
        {phone && (
          <ProviderContactLink className="provider-phone-link" to={`tel:${phone}`}>
            {phone}
          </ProviderContactLink>
        )}
        {website && (
          <ProviderContactLink className="provider-website-link" to={website} target="_blank" rel="noreferrer noopener">
            <FormattedMessage defaultMessage="Visit website" />
          </ProviderContactLink>
        )}
        {address && <DirectionsLink address={address} searchLocation={searchLocation} />}
      </Stack>
    </FlexContainer>
  )

  const distance = (
    <FlexContainer
      justifyContent={[`${carrotPartner ? "center" : "flex-start"}`, null, "center"]}
      minWidth="129px"
      padding={["0", null, `${carrotPartner ? "0" : "48px 0 0 0 "}`]}
    >
      <Stack direction="row" spacing="4px">
        <Container padding="2px 0 0 0">
          <Icon name="locationArrow" size="tiny" color="carrotOrange" />
        </Container>
        <LocalizedDistance distanceInKm={distanceInKm} />
      </Stack>
    </FlexContainer>
  )

  const bookButtonAndDistance = (
    <Stack
      direction="column"
      alignItems="center"
      padding={["16px 0 0 0", null, "31px 0 0 0"]}
      spacing="4px"
      width="fit-content"
    >
      <Link
        className="provider-book-button"
        to={`/talk-to-carrot/sendmessage/bookappointment/provider/${encodeURIComponent(providerName)}`}
      >
        <BookingButton variant="alternate">
          <FlexContainer alignItems="center" direction="row" minWidth="65px" gap="4px">
            <Icon name="providerCalendar" color="carrotOrange" size="small" />
            <FormattedMessage
              defaultMessage="Book"
              description="Clicking on the Book button will redirect you to directions on how to book an appointment at a provider"
            />
          </FlexContainer>
        </BookingButton>
      </Link>
      {distance}
    </Stack>
  )

  const carrotPartnerBadgeSpacer = useBreakpointComponent([
    <></>,
    <></>,
    <Container width="24px" key="large" />,
    <Container width="24px" key="x-large" />
  ])

  const carrotPartnerBadge = carrotPartner ? (
    <Stack padding={["0", null, "44px 0 0 0"]}>
      <img src={carrotPartnerIndicator} alt={intl.formatMessage({ defaultMessage: "Carrot Partner indicator" })} />
    </Stack>
  ) : (
    carrotPartnerBadgeSpacer
  )

  return (
    <FlexContainer
      gap={["12px", null, "tiny"]}
      direction={["column", null, "row"]}
      data-provider-search-result-rank={rank}
      data-provider-uuid={provider.providerUuid}
    >
      {carrotPartnerBadge}
      <FlexContainer
        width="100%"
        justifyContent="space-between"
        gap={["12px", null, "large"]}
        direction={["column", null, "row"]}
      >
        {mainContent}
        {carrotPartner ? bookButtonAndDistance : distance}
      </FlexContainer>
    </FlexContainer>
  )
}
